import React, { useState } from "react";
import Modal from "react-modal";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { t } from "localizify";

// Validation Schema
const validationSchema = yup.object().shape({
  card: yup
    .string()
    .required(t("Card number is required"))
    .test("no-spaces", t("Card number is invalid"), (value) => value.trim() !== ""),
  exp: yup
    .string()
    .required(t("Expiry date is required"))
    .test("no-spaces", t("Expiry date is invalid"), (value) => value.trim() !== ""),
  cvv: yup
    .string()
    .required(t("CVV is required"))
    .test("no-spaces", t("CVV is invalid"), (value) => value.trim() !== ""),
});

const PaymentModal = ({
  paymentModalIsOpen,
  setPaymentModalIsOpen,
  name,
  setName,
  lastname,
  setLastname,
  card,
  setCard,
  exp,
  setExp,
  cvv,
  setCvv,
  totalAmount,
  checkOrderSummary,
}) => {
  const publicUrl = process.env.PUBLIC_URL;

  const COMMON_STYLE_FOR_POPUP = () => ({
     overlay: {
      backgroundColor: "rgba(0, 0, 0, 0.5)", 
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 9999,
    },
    content: {
      position: "relative",
      inset: "unset",
      background: "#fff",
      border: "none",
      borderRadius: "16px",
      width: "60%",           
      maxWidth: "900px",     
      padding: "30px 40px",
      overflow: "auto",
      boxShadow: "0 10px 30px rgba(0, 0, 0, 0.2)",
    },
  });

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      card: card || "",
      exp: exp || "",
      cvv: cvv || "",
    },
  });

  const onSubmit = () => {
    if (checkOrderSummary) {
      checkOrderSummary();
    }
  };

  return (
    <Modal
      isOpen={paymentModalIsOpen}
      onRequestClose={() => setPaymentModalIsOpen(false)}
      style={COMMON_STYLE_FOR_POPUP()}
    >
      <div className="modal-dialog modal-dialog-centered payment-modal" style={{ maxWidth: "1000px" }}>
        <div className="modal-content border-0">
          <div className="row">
            <div className="col-md-1">
              <button type="button" className="modal_back" onClick={() => setPaymentModalIsOpen(false)}>
                <img
                  src={publicUrl + "/assets/images/icons/go-back.svg"}
                  className="img-fluid"
                  alt="back"
                />
              </button>
            </div>
            <div className="col-md-11 d-flex justify-content-center align-items-center">
              <h4>{t("Process Payment")}</h4>
            </div>
          </div>
          <div className="modal-body" style={{ maxHeight: "80vh", overflowY: "auto" }}>
            <div className="forgot-modal-card">
              <div className="mt-4">
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="form-row mb-3 d-flex">
                    <div className="col-md-6 pr-2" style={{ marginBottom: "15px" }}>
                      <label htmlFor="firstName" className="form-label">{t("First Name")}</label>
                      <input
                        type="text"
                        className="form-control"
                        id="firstName"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder={t("Enter your first name")}
                      />
                    </div>
                    <div className="col-md-6 px-2" style={{ marginBottom: "15px" }}>
                      <label htmlFor="lastName" className="form-label">{t("Last Name")}</label>
                      <input
                        type="text"
                        className="form-control"
                        id="lastName"
                        value={lastname}
                        onChange={(e) => setLastname(e.target.value)}
                        placeholder={t("Enter your last name")}
                      />
                    </div>
                  </div>
                  <div className="form-row mb-3 d-flex">
                    <div className="col-md-6 pr-2" style={{ marginBottom: "15px" }}>
                      <label htmlFor="cardNumber" className="form-label">{t("Card Number")}</label>
                      <Controller
                        name="card"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="text"
                            className="form-control"
                            id="cardNumber"
                            placeholder={t("Enter card number")}
                            onChange={(e) => [setCard(e.target.value), setValue('card', e.target.value)]}
                          />
                        )}
                      />
                      {errors.card && <span className="text-danger">{errors.card.message}</span>}
                    </div>
                    <div className="col-md-4 px-2" style={{ marginBottom: "15px" }}>
                      <label htmlFor="expiryDate" className="form-label">{t("Expiry Date")}</label>
                      <Controller
                        name="exp"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="text"
                            className="form-control"
                            id="expiryDate"
                            placeholder="MM/YY"
                            onChange={(e) => [setExp(e.target.value), setValue('exp', e.target.value)]}
                          />
                        )}
                      />
                      {errors.exp && <span className="text-danger">{errors.exp.message}</span>}
                    </div>
                    <div className="col-md-2 pl-2" style={{ marginBottom: "15px" }}>
                      <label htmlFor="cvv" className="form-label">{t("CVV")}</label>
                      <Controller
                        name="cvv"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="text"
                            className="form-control"
                            id="cvv"
                            placeholder={t("CVV")}
                            onChange={(e) => [setCvv(e.target.value), setValue('cvv', e.target.value)]}
                          />
                        )}
                      />
                      {errors.cvv && <span className="text-danger">{errors.cvv.message}</span>}
                    </div>
                  </div>
                  <div className="form-row mb-3 d-flex justify-content-around">
                    <button type="submit" className="btn btn-success">
                      {t("Pay")} ${totalAmount}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default PaymentModal;
